import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { FlagPip } from "../components/StageBadge";
import RoomDrawer from "../components/RoomDrawer";
import MobileFilterPanel from "../components/MobileFilterPanel";
import { repairLocation } from "../lib/location";
import { useApplication } from "../context/ApplicationContext";

const CONSIDERATIONS = [
  { key: "rebooking", label: "Rebookers", match: (room) => room.flags?.rebooking },
  { key: "storage", label: "Storage", match: (room) => room.flags?.storage },
  { key: "summer_stay", label: "Summer Stay", match: (room) => room.flags?.summer_stay },
  { key: "refurbishment", label: "Refurbishment", match: (room) => room.flags?.refurbishment },
  { key: "priority", label: "Priority", match: (room) => room.priority },
];

function roomConsiderations(room) {
  return CONSIDERATIONS.filter((item) => item.match(room));
}

export default function SpecialConsiderations() {
  const [rooms, setRooms] = useState([]);
  const [considerationFilter, setConsiderationFilter] = useState("all");
  const [roomTypeFilter, setRoomTypeFilter] = useState("all");
  const [blockFilter, setBlockFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const {effectiveWorkflow}=useApplication();
  const stageLabels=useMemo(()=>Object.fromEntries((effectiveWorkflow?.stages||[]).map(stage=>[stage.key,stage.display_name])),[effectiveWorkflow]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/rooms");
    setRooms(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const specialRooms = useMemo(
    () => rooms.map((room) => ({ ...room, considerations: roomConsiderations(room) })).filter((room) => room.considerations.length > 0),
    [rooms],
  );

  const counts = useMemo(() => {
    const out = { all: specialRooms.length };
    for (const item of CONSIDERATIONS) out[item.key] = specialRooms.filter((room) => item.match(room)).length;
    return out;
  }, [specialRooms]);

  const blockOptions = useMemo(() => {
    const byId = new Map();
    for (const room of specialRooms) {
      if (room.block_id) byId.set(room.block_id, room.block_name || room.block_id);
    }
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [specialRooms]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return specialRooms.filter((room) => {
      if (considerationFilter !== "all" && !room.considerations.some((item) => item.key === considerationFilter)) return false;
      if (roomTypeFilter !== "all" && room.type !== roomTypeFilter) return false;
      if (blockFilter !== "all" && room.block_id !== blockFilter) return false;
      if (!term) return true;
      return [
        room.site_name,
        room.block_name,
        room.flat_name,
        room.name,
        stageLabels[room.current_stage],
        ...room.considerations.map((item) => item.label),
      ].some((value) => String(value || "").toLowerCase().includes(term));
    });
  }, [specialRooms, considerationFilter, roomTypeFilter, blockFilter, search, stageLabels]);

  const grouped = useMemo(() => {
    const groups = [];
    const byKey = new Map();
    const sorted = [...filtered].sort((a, b) => (
      `${a.block_name || ""}|${a.flat_name || ""}|${a.type === "common" ? 0 : 1}|${a.name || ""}`
        .localeCompare(`${b.block_name || ""}|${b.flat_name || ""}|${b.type === "common" ? 0 : 1}|${b.name || ""}`)
    ));
    for (const room of sorted) {
      const key = `${room.site_id || ""}|${room.block_id || ""}|${room.flat_id || room.flat_name || ""}`;
      if (!byKey.has(key)) {
        const group = {
          key,
          site: room.site_name || "Unknown site",
          block: room.block_name || "—",
          flat: room.flat_name || "—",
          rooms: [],
        };
        byKey.set(key, group);
        groups.push(group);
      }
      byKey.get(key).rooms.push(room);
    }
    return groups;
  }, [filtered]);

  return (
    <div data-testid="special-considerations-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-chivo font-black text-4xl tracking-tight">Special Considerations</h1>
          <p className="text-sm text-gray-600 mt-1">Review rebookers, storage rooms, summer stays, refurbishments and priority rooms in one list.</p>
        </div>
      </div>

      <MobileFilterPanel className="bg-white border border-gray-200 p-3 mb-4 flex-col gap-3" testId="special-filters">
        <div className="flex flex-wrap gap-2">
          <FilterButton active={considerationFilter === "all"} onClick={() => setConsiderationFilter("all")} label={`All (${counts.all || 0})`} testId="special-filter-all" />
          {CONSIDERATIONS.map((item) => (
            <FilterButton
              key={item.key}
              active={considerationFilter === item.key}
              onClick={() => setConsiderationFilter(item.key)}
              label={`${item.label} (${counts[item.key] || 0})`}
              testId={`special-filter-${item.key}`}
            />
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <FilterButton active={roomTypeFilter === "all"} onClick={() => setRoomTypeFilter("all")} label="All" testId="special-room-type-all" />
          <FilterButton active={roomTypeFilter === "bedroom"} onClick={() => setRoomTypeFilter("bedroom")} label="Bedrooms" testId="special-room-type-bedroom" />
          <FilterButton active={roomTypeFilter === "common"} onClick={() => setRoomTypeFilter("common")} label="Common" testId="special-room-type-common" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <label className="text-xs font-mono uppercase tracking-wider text-gray-500">
            Block
            <select value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)} className="mt-1 w-full border border-gray-300 px-2 py-2 text-sm bg-white">
              <option value="all">All blocks</option>
              {blockOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
          <label className="text-xs font-mono uppercase tracking-wider text-gray-500">
            Search
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mt-1 w-full border border-gray-300 px-2 py-2 text-sm"
              placeholder="Site, block, flat, room or consideration"
            />
          </label>
        </div>
      </MobileFilterPanel>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 p-8 text-center text-sm text-gray-500">No special consideration rooms match these filters.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {grouped.map((group) => (
            <div key={group.key} className="contents">
              {group.rooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoomId(room.id)}
                  data-testid={`special-room-${room.id}`}
                  className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-400 hover:shadow"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {room.considerations.map((item) => (
                      item.key === "priority"
                        ? <span key={item.key} className="flag-pip" style={{ color: "#DC2626" }} data-testid={`flag-priority-${room.id}`}>Priority</span>
                        : <FlagPip key={item.key} kind={item.key} />
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-base font-bold">{repairLocation({...room,room_name:room.name}).primary}</div>
                    {room.type === "common" && <span className="text-[9px] font-mono font-bold text-gray-500 uppercase border border-gray-300 px-1">Common</span>}
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase">{stageLabels[room.current_stage]||"Workflow stage"}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {repairLocation({...room,room_name:room.name}).secondary}
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {selectedRoomId && (
        <RoomDrawer
          roomId={selectedRoomId}
          onClose={() => {
            setSelectedRoomId(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function FilterButton({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs font-mono font-bold uppercase border ${active ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"}`}
    >
      {label}
    </button>
  );
}
